import { useRequestMagicLink } from '@/api/auth/auth.hooks.ts';
import { useLoginForm } from '@/forms/login.form';
import { emailSchema } from '@/validation/email.schema';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContainer } from '../components/auth-container.component';

const inputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'white',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
    '&:hover': { bgcolor: 'white' },
    '&.Mui-focused': { bgcolor: 'white' },
  },
};

const disabledInputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'rgba(255,255,255,0.6)',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
  },
};

export function AuthLoginPage() {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const requestMagicLink = useRequestMagicLink();

  const handleMagicLink = () => {
    if (!email) return;
    // The response is identical whether or not the account exists, so a
    // transport error is treated the same as success: show the confirmation.
    requestMagicLink.mutate(
      { email },
      {
        onSuccess: () => setMagicLinkSent(true),
        onError: () => setMagicLinkSent(true),
      },
    );
  };

  const handleForgotPassword = () => {
    navigate(
      email
        ? `/auth/forgot-password?email=${encodeURIComponent(email)}`
        : '/auth/forgot-password',
    );
  };

  const { register, onSubmit, errors, isPending, setValue, setError } =
    useLoginForm({
      onSuccess: (response) => {
        if (response.accessToken) {
          signIn({
            auth: {
              token: response.accessToken,
              type: 'Bearer',
            },
          });
          navigate(`/`);
        }
      },
      onError: (error: AxiosError<{ message: string }>) => {
        if (error?.response?.status === 400) {
          setError('password', {
            message: error.response.data.message,
          });
          return;
        }
        setError('password', {
          message:
            'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
        });
      },
    });

  const handleBackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    navigate('/auth');
  };

  useEffect(() => {
    if (!emailSchema.safeParse(email).success) {
      navigate('/auth');
      return;
    }
    setValue('email', email as unknown as string);
  }, [email, navigate, setValue]);

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: '#02344C',
          fontWeight: 700,
          mb: 0.75,
          textAlign: 'center',
          letterSpacing: '-0.01em',
        }}
      >
        Anmelden
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
          textAlign: 'center',
          maxWidth: 340,
          lineHeight: 1.6,
        }}
      >
        Geben Sie Ihr Passwort ein, um fortzufahren.
      </Typography>
      <AuthContainer component="form" onSubmit={onSubmit}>
        <Tooltip
          title="Um die E-Mail-Adresse zu ändern, gehen Sie bitte zurück zur vorherigen Seite."
          arrow
          placement="top"
        >
          <TextField
            id="email"
            label="E-Mail Adresse"
            type="email"
            variant="filled"
            fullWidth
            required
            disabled
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={disabledInputSx}
          />
        </Tooltip>
        <TextField
          id="password"
          label="Passwort"
          type="password"
          variant="filled"
          fullWidth
          autoFocus
          required
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
          sx={inputSx}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            gap: 1.5,
            mt: 1,
          }}
        >
          <Button
            onClick={handleBackClick}
            fullWidth
            variant="outlined"
            disabled={isPending}
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Zurück
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isPending}
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Weiter {isPending && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Button>
        </Box>

        <Link
          component="button"
          type="button"
          onClick={handleForgotPassword}
          underline="hover"
          sx={{
            color: 'text.secondary',
            fontSize: '0.85rem',
            fontWeight: 500,
          }}
        >
          Passwort vergessen?
        </Link>

        <Divider flexItem sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          oder
        </Divider>

        {magicLinkSent ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 340,
              lineHeight: 1.6,
            }}
          >
            Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen
            einen Anmeldelink gesendet.
          </Typography>
        ) : (
          <Button
            onClick={handleMagicLink}
            fullWidth
            variant="outlined"
            disabled={requestMagicLink.isPending}
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Anmeldelink per E-Mail erhalten{' '}
            {requestMagicLink.isPending && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Button>
        )}
      </AuthContainer>
    </>
  );
}
